"use client";

/**
 * Limite d'erreur du segment localisé. Le dépôt n'en avait aucune : toute
 * exception levée pendant le rendu remplaçait la page entière par l'écran
 * technique de Next, y compris pour une broutille décorative comme un compteur
 * de la barre du haut lisant une session incomplète.
 *
 * Volontairement AUTONOME, et c'est la seule exception assumée à la règle
 * « tout est traduit par next-intl » : ce composant doit s'afficher même quand
 * ce qui a planté est justement le contexte de traduction ou le fournisseur de
 * session. Il ne lit donc que le paramètre de route (`useParams`, fourni par
 * Next lui-même) et n'importe aucun composant du design system, pour qu'aucune
 * dépendance ne puisse le faire tomber à son tour. Un écran de secours qui
 * plante ne sert à rien.
 */
import { useEffect } from "react";
import { useParams } from "next/navigation";

const COPY = {
    fr: {
        title: "Une erreur est survenue",
        description:
            "Cette page n'a pas pu s'afficher. Le problème est souvent passager : réessayez, ou revenez à l'accueil.",
        retry: "Réessayer",
        home: "Retourner à l'accueil",
    },
    en: {
        title: "Something went wrong",
        description:
            "This page could not be displayed. The problem is usually temporary: try again, or go back to the homepage.",
        retry: "Try again",
        home: "Return to the homepage",
    },
};

export default function LocaleError({ error, reset }) {
    const params = useParams();
    const locale = params?.locale === "en" ? "en" : "fr";
    const t = COPY[locale];

    useEffect(() => {
        // Trace console : sans ça, une exception de rendu ne laisse aucune
        // piste côté client une fois l'écran de secours affiché.
        console.error("[error boundary]", error);
    }, [error]);

    return (
        <main className="flex min-h-screen flex-col items-center justify-center gap-6 px-6 text-center">
            <div>
                <h1 className="text-2xl font-bold text-[#06050F] dark:text-white">
                    {t.title}
                </h1>
                <p className="mt-3 max-w-md text-base text-[#06050F]/60 dark:text-lightBlue mb-0">
                    {t.description}
                </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3">
                <button
                    type="button"
                    onClick={() => reset()}
                    className="rounded-xl bg-[#06050F] px-5 py-3 text-sm font-bold text-white dark:bg-white dark:text-[#0E161D]"
                >
                    {t.retry}
                </button>
                <a
                    href={`/${locale}`}
                    className="rounded-xl border border-gray-200 px-5 py-3 text-sm font-bold text-[#06050F] dark:border-grayDark-700 dark:text-white"
                >
                    {t.home}
                </a>
            </div>
        </main>
    );
}
